import { Fragment } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import SharedAsset from '@zenbusiness/common-components/SharedAsset';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';

import Skeleton from '#client/components/Skeleton';
import { isReserved } from '#client/utils/businessEntity';
import { buildCartUrl } from '#client/utils/urls';

import AmendmentBanner from '../Amendments/AmendmentBanner';
import {
  sectionsInfo,
  managerTypeDesc,
  registrationsInfo
} from './helpers';
import {
  Root,
  ItemTitle,
  ItemTitleSubLabel,
  TextTitleSubLabel,
  TooltipContent,
  Section,
  SectionContainer,
  SectionContent,
  SectionItems,
  SectionTitle,
  ProfileHead,
  TooltipWithStyles,
  TooltipLabel
} from './useStyles';

export const businessTypes = {
  llc: 'Limited Liability Company',
  corp: 'Corporation',
  sole_prop: 'Sole Proprietorship'
};

const NewBusinessProfile = ({
  loading,
  businessEntity = {},
  virtualAddress = {},
  entitlements = {},
  pricing = [],
  registeredAgents = []
}) => {
  const theme = useTheme();
  const { palette } = theme;
  const { businessEntityUuid } = useBusinessEntity();
  const {
    businessEntityName: name,
    businessEntityType: type,
    businessEntityType,
    businessIndustry: industry,
    physicalAddress,
    mailingAddress,
    principalAddress,
    businessEntityData: {
      authorizedSignatory: signatory,
      businessPurpose: purpose,
      directors = [],
      domesticJurisdiction,
      ein,
      managerType,
      managers = [],
      members = [],
      officers = [],
      officialContact,
      sopContact: sop
    } = {}
  } = businessEntity;

  const hasVba = entitlements.has('virtual_business_address');
  const formationEntitlement = entitlements.get(`${type}_formation`);
  const formationFulfillment = formationEntitlement?.fulfillment;
  const isFormationCompleted = formationFulfillment?.derivedStatus === 'active';
  const isReservedEntity = isReserved(businessEntity, entitlements);
  const amendmentPricingObj = pricing.find(({ service }) => service === `${type}_amendment`);
  const amendmentPricing = amendmentPricingObj?.amount;
  const registrations = registrationsInfo(registeredAgents, domesticJurisdiction)[0] || [];

  const sections = sectionsInfo(
    isReservedEntity,
    name,
    businessTypes,
    type,
    ein,
    purpose,
    industry,
    registrations,
    domesticJurisdiction,
    managerType,
    managerTypeDesc,
    directors,
    officers,
    managers,
    members,
    registeredAgents,
    physicalAddress,
    mailingAddress,
    principalAddress,
    signatory,
    officialContact,
    sop,
    virtualAddress,
    hasVba
  );

  return (
    <Root>
      <ProfileHead level={1} variant="md">{`${isReservedEntity ? 'Reserved ' : ''}Business Profile`}</ProfileHead>
      {isFormationCompleted && (
        <AmendmentBanner
          amendmentUrl={buildCartUrl(`${businessEntityType}_amendment`, { businessEntityUuid })}
          amendmentPricing={amendmentPricing}
        />
      )}
      <SectionContainer container>
        {sections
          .map(({
            titleSection, subSections, largeRow, hide
          }, i) => (
            <Fragment key={`businessProfileTitleSection-${i.toString()}`}>
              {!hide && (
                <>
                  <SectionTitle variant="sm">{titleSection}</SectionTitle>
                  <SectionItems
                    sx={{
                      [theme.breakpoints.up('md')]: {
                        gridTemplateColumns: largeRow || '33% 33% 33%'
                      }
                    }}
                  >
                    {subSections
                      .filter(({ filter }) => (loading || filter !== false))
                      .map(({
                        title, titleTooltip, items, widths, threeColumns
                      }, j) => (
                        <Section item key={`businessProfileSubsection-${j.toString()}`} {...widths}>
                          {loading && <Skeleton />}
                          {!loading && (
                            <>
                              {!titleTooltip && <ItemTitleSubLabel size="md">{title}</ItemTitleSubLabel>}
                              {titleTooltip && (
                                <TooltipWithStyles
                                  placement="top-start"
                                  title={(
                                    <TooltipContent>
                                      {titleTooltip}
                                    </TooltipContent>
                                  )}
                                  enterTouchDelay={0}
                                  leaveTouchDelay={15000}
                                >
                                  <TooltipLabel>
                                    <ItemTitleSubLabel size="md">{title}</ItemTitleSubLabel>
                                    <SharedAsset src="/icon/micro/info-circle-alt.svg" width={16} coRGB={palette.Wisdom.main} alt="tip" />
                                  </TooltipLabel>
                                </TooltipWithStyles>
                              )}
                            </>
                          )}

                          <SectionContent
                            container
                            sx={{
                              [theme.breakpoints.up('md')]: {
                                gridTemplateColumns: threeColumns || '1fr'
                              }
                            }}
                          >
                            {items.map(({
                              content, label, tooltip, useToolTipDangerColor, ...props
                            }, k) => (
                              <Grid item {...props} key={`businessProfileItem-${k.toString()}`}>
                                {loading && <Skeleton />}

                                {(!loading && label) && (
                                  <ItemTitle>
                                    <TextTitleSubLabel size="sm">
                                      {label}
                                    </TextTitleSubLabel>
                                    {tooltip && (
                                      <TooltipWithStyles
                                        placement="top-start"
                                        title={(
                                          <TooltipContent>
                                            {tooltip}
                                          </TooltipContent>
                                        )}
                                        enterTouchDelay={0}
                                        leaveTouchDelay={15000}
                                      >
                                        <TooltipLabel>
                                          <SharedAsset src="/icon/micro/info-circle-alt.svg" width={16} coRGB={palette.Wisdom.main} alt="tip" />
                                        </TooltipLabel>
                                      </TooltipWithStyles>
                                    )}
                                  </ItemTitle>
                                )}
                                {content}
                              </Grid>
                            ))}
                          </SectionContent>
                        </Section>
                      ))}
                  </SectionItems>
                </>
              )}
            </Fragment>
          ))}
      </SectionContainer>
    </Root>
  );
};

NewBusinessProfile.propTypes = {
  virtualAddress: PropTypes.shape({}),
  loading: PropTypes.bool.isRequired,
  businessEntity: PropTypes.shape({}).isRequired,
  entitlements: PropTypes.shape({}).isRequired,
  pricing: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  registeredAgents: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

NewBusinessProfile.defaultProps = {
  virtualAddress: {}
};

export default NewBusinessProfile;
